import styled from 'styled-components';
import { useGetPtoRevisionQuery, type PtoRdRevision } from '@shared/api';
import { revisionFlagNotes, type RevisionFlagNote } from './revisionFlags';

const List = styled.div<{ $compact: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin: ${({ $compact, theme }) => ($compact ? `${theme.spacing(1.5)} 0 0` : `0 0 ${theme.spacing(3)}`)};
`;

const Note = styled.div<{ $tone: RevisionFlagNote['tone']; $compact: boolean }>`
  font-size: ${({ $compact }) => ($compact ? '12.5px' : '13px')};
  line-height: 1.5;
  padding: ${({ $compact, theme }) =>
    $compact ? `0 0 0 ${theme.spacing(2)}` : `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-left: 3px solid
    ${({ $tone, theme }) => ($tone === 'danger' ? theme.colors.status.danger : theme.colors.status.warning)};
  border-radius: ${({ $compact, theme }) => ($compact ? '0' : theme.radius.sm)};
  background: ${({ $tone, $compact, theme }) =>
    $compact
      ? 'transparent'
      : `${$tone === 'danger' ? theme.colors.status.danger : theme.colors.status.warning}14`};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Title = styled.div`
  font-weight: 600;
`;

const Hint = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Details = styled.ul`
  margin: ${({ theme }) => theme.spacing(1)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Предупреждения о полноте последней индексации ревизии (AERO A2): «OCR
 *  обрезан», «разбор таблиц упал», «позиции не заменены». `compact` — строкой
 *  под ревизией в списке, иначе — баннером над таблицей позиций. */
export function RevisionFlagNotes({
  revision,
  compact = false,
}: {
  revision: PtoRdRevision;
  compact?: boolean;
}) {
  const notes = revisionFlagNotes(revision);
  if (notes.length === 0) return null;
  return (
    <List $compact={compact} role="status">
      {notes.map((n) => (
        <Note key={n.key} $tone={n.tone} $compact={compact} title={compact ? n.hint : undefined}>
          <Title>{n.title}</Title>
          {!compact && <Hint>{n.hint}</Hint>}
          {n.details.length > 0 && (
            <Details>
              {n.details.map((d, i) => (
                <li key={`${n.key}:${i}`}>{d}</li>
              ))}
            </Details>
          )}
        </Note>
      ))}
    </List>
  );
}

/** Баннер экрана «Объёмы» ревизии: флаги последней индексации (виден и
 *  читателю — ревизия запрашивается без права записи). */
export function RevisionFlagsBanner({ revisionId }: { revisionId: string }) {
  const { data } = useGetPtoRevisionQuery(revisionId, { skip: !revisionId });
  if (!data?.revision) return null;
  return <RevisionFlagNotes revision={data.revision} />;
}
